/**
 * Customer Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  CustomerEntity,
  CustomerSearchItemEntity,
  CustomerGroupEntity,
} from "../../domain/entities/CustomerEntity";
import {
  CustomerDataDTO,
  CustomerSearchItemDTO,
  CustomerGroupDTO,
} from "../models/CustomerDTOs";

export class CustomerMapper {
  /**
   * Map CustomerDataDTO to CustomerEntity
   */
  static toCustomerEntity(dto: CustomerDataDTO): CustomerEntity {
    // Handle both customerid and customer_id field names from API
    const customerId = (dto as any).customerid || (dto as any).customer_id;

    return {
      customerid: customerId,
      zoho_customerid: dto.zoho_customerid,
      customer_name: dto.customer_name,
      // customer_type: dto.customer_type,
      gst_treatment: dto.gst_treatment,
      // gst_customertype: dto.gst_customertype,
      gstin_vat_tax_id: dto.gstin_vat_tax_id,
      pan_tax_registration_no: dto.pan_tax_registration_no,
      contact_name_1: dto.contact_name_1,
      contact_number_1: dto.contact_number_1,
      contact_email_1: dto.contact_email_1,
      contact_name_2: undefined, // Not in DTO
      contact_number_2: undefined, // Not in DTO
      contact_email_2: dto.contact_email_2,
      communication_email_id: dto.communication_email_id,
      billing_address: dto.billing_address,
      shipping_address: undefined, // Not in DTO
      city: dto.city,
      state: dto.state,
      country: dto.country,
      postal_code: dto.postal_code,
      credit_limit: dto.credit_limit,
      payment_terms: dto.payment_terms,
      currency_id: dto.currency_id,
      bank_account_details: undefined, // Not in DTO
      group_id: dto.group_id,
      status: dto.status,
      refresh_token: dto.refresh_token,
      customer_name_embedding: undefined, // Not in DTO
      created_at: undefined, // Not in DTO
      updated_at: undefined, // Not in DTO
    };
  }

  /**
   * Map CustomerEntity to CustomerDataDTO (for create/update)
   */
  static toCustomerDataDTO(entity: Partial<CustomerEntity>): CustomerDataDTO {
    return {
      customerid: entity.customerid,
      zoho_customerid: entity.zoho_customerid,
      customer_name: entity.customer_name || "",
      // customer_type: entity.customer_type || "",
      gst_treatment: entity.gst_treatment || "business_gst",
      // gst_customertype: entity.gst_customertype || "b2b",
      gstin_vat_tax_id: entity.gstin_vat_tax_id,
      pan_tax_registration_no: entity.pan_tax_registration_no,
      contact_name_1: entity.contact_name_1 || "",
      contact_number_1: entity.contact_number_1 || "",
      contact_email_1: entity.contact_email_1 || "",
      contact_email_2:
        (entity.contact_email_2 as string | undefined) || undefined,
      communication_email_id: entity.communication_email_id || "",
      billing_address: entity.billing_address || "",
      city: entity.city || "",
      state: entity.state || "",
      country: entity.country || "",
      postal_code: entity.postal_code || "",
      credit_limit: entity.credit_limit,
      payment_terms: entity.payment_terms || 0,
      currency_id: entity.currency_id || "",
      group_id: entity.group_id,
      status: entity.status,
      refresh_token: entity.refresh_token,
    };
  }

  /**
   * Map CustomerSearchItemDTO to CustomerSearchItemEntity
   */
  static toCustomerSearchItemEntity(
    dto: CustomerSearchItemDTO
  ): CustomerSearchItemEntity {
    return {
      customer_id: parseInt(dto.customer_id, 10),
      zoho_customerid: dto.zoho_customerid,
      customer_name: dto.customer_name,
      customer_type: dto.customer_type,
      gst_treatment: dto.gst_treatment,
      gst_customertype: dto.gst_customertype,
      gstin_vat_tax_id: dto.gstin_vat_tax_id,
      pan_tax_registration_no: dto.pan_tax_registration_no,
      contact_name_1: dto.contact_name_1,
      contact_number_1: dto.contact_number_1,
      contact_email_1: dto.contact_email_1,
      contact_name_2: dto.contact_name_2 ?? undefined,
      contact_number_2: dto.contact_number_2 ?? undefined,
      contact_email_2: dto.contact_email_2 ?? undefined,
      communication_email_id: dto.communication_email_id,
      billing_address: dto.billing_address,
      shipping_address: dto.shipping_address ?? undefined,
      city: dto.city,
      state: dto.state,
      country: dto.country,
      postal_code: dto.postal_code,
      credit_limit: dto.credit_limit,
      payment_terms: dto.payment_terms,
      currency_id: dto.currency_id,
      bank_account_details: dto.bank_account_details ?? undefined,
      status: dto.status,
      customer_name_embedding: dto.customer_name_embedding,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  /**
   * Map CustomerGroupDTO to CustomerGroupEntity
   */
  static toCustomerGroupEntity(dto: CustomerGroupDTO): CustomerGroupEntity {
    return {
      group_id: dto.group_id,
      group_name: dto.group_name,
      group_description: dto.description ?? undefined,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  // Legacy methods for backward compatibility (will be removed after refactor)
  /**
   * @deprecated Use toCustomerEntity instead
   */
  static toEntity(apiData: any): CustomerEntity {
    return {
      customerid: apiData.customerid || apiData.customer_id,
      zoho_customerid: apiData.zoho_customerid,
      customer_name: apiData.customer_name,
      // customer_type: apiData.customer_type,
      gst_treatment: apiData.gst_treatment,
      // gst_customertype: apiData.gst_customertype,
      gstin_vat_tax_id: apiData.gstin_vat_tax_id,
      pan_tax_registration_no: apiData.pan_tax_registration_no,
      contact_name_1: apiData.contact_name_1,
      contact_number_1: apiData.contact_number_1,
      contact_email_1: apiData.contact_email_1,
      contact_name_2: apiData.contact_name_2,
      contact_number_2: apiData.contact_number_2,
      contact_email_2: apiData.contact_email_2,
      communication_email_id: apiData.communication_email_id,
      billing_address: apiData.billing_address,
      shipping_address: apiData.shipping_address,
      city: apiData.city,
      state: apiData.state,
      country: apiData.country,
      postal_code: apiData.postal_code,
      credit_limit: apiData.credit_limit,
      payment_terms: apiData.payment_terms,
      currency_id: apiData.currency_id,
      bank_account_details: apiData.bank_account_details,
      group_id: apiData.group_id,
      status: apiData.status,
      refresh_token: apiData.refresh_token,
      customer_name_embedding: apiData.customer_name_embedding,
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
    };
  }

  /**
   * @deprecated Use toCustomerDataDTO instead
   */
  static toDTO(entity: Partial<CustomerEntity>): any {
    return {
      customerid: entity.customerid,
      zoho_customerid: entity.zoho_customerid,
      customer_name: entity.customer_name,
      // customer_type: entity.customer_type,
      gst_treatment: entity.gst_treatment,
      // gst_customertype: entity.gst_customertype,
      gstin_vat_tax_id: entity.gstin_vat_tax_id,
      pan_tax_registration_no: entity.pan_tax_registration_no,
      contact_name_1: entity.contact_name_1,
      contact_number_1: entity.contact_number_1,
      contact_email_1: entity.contact_email_1,
      contact_name_2: entity.contact_name_2,
      contact_number_2: entity.contact_number_2,
      contact_email_2: entity.contact_email_2,
      communication_email_id: entity.communication_email_id,
      billing_address: entity.billing_address,
      shipping_address: entity.shipping_address,
      city: entity.city,
      state: entity.state,
      country: entity.country,
      postal_code: entity.postal_code,
      credit_limit: entity.credit_limit,
      payment_terms: entity.payment_terms,
      currency_id: entity.currency_id,
      bank_account_details: entity.bank_account_details,
      group_id: entity.group_id,
      status: entity.status,
      refresh_token: entity.refresh_token,
    };
  }

  /**
   * @deprecated Use toCustomerSearchItemEntity instead
   */
  static toSearchItemEntity(apiItem: any): CustomerSearchItemEntity {
    return {
      customer_id: apiItem.customer_id || apiItem.customerid,
      zoho_customerid: apiItem.zoho_customerid,
      customer_name: apiItem.customer_name,
      customer_type: apiItem.customer_type,
      gst_treatment: apiItem.gst_treatment,
      gst_customertype: apiItem.gst_customertype,
      gstin_vat_tax_id: apiItem.gstin_vat_tax_id,
      pan_tax_registration_no: apiItem.pan_tax_registration_no,
      contact_name_1: apiItem.contact_name_1,
      contact_number_1: apiItem.contact_number_1,
      contact_email_1: apiItem.contact_email_1,
      contact_name_2: apiItem.contact_name_2,
      contact_number_2: apiItem.contact_number_2,
      contact_email_2: apiItem.contact_email_2,
      communication_email_id: apiItem.communication_email_id,
      billing_address: apiItem.billing_address,
      shipping_address: apiItem.shipping_address,
      city: apiItem.city,
      state: apiItem.state,
      country: apiItem.country,
      postal_code: apiItem.postal_code,
      credit_limit: apiItem.credit_limit,
      payment_terms: apiItem.payment_terms,
      currency_id: apiItem.currency_id,
      bank_account_details: apiItem.bank_account_details,
      status: apiItem.status,
      customer_name_embedding: apiItem.customer_name_embedding,
      created_at: apiItem.created_at,
      updated_at: apiItem.updated_at,
    };
  }
}
